import {
	Assignment as AssignmentIcon,
	CheckCircle as CheckCircleIcon,
	AccessTime as ClockIcon,
	Cancel as XCircleIcon
} from "@mui/icons-material";
import { Box, Card, CardContent, CircularProgress, Grid2, Skeleton, Typography } from "@mui/material";
import {
	Document,
	Packer,
	Paragraph,
	Table,
	TableCell,
	TableRow,
	TextRun,
	ImageRun,
	WidthType,
	AlignmentType,
	BorderStyle,
	HeadingLevel,
	VerticalAlignTable,
	convertInchesToTwip
} from "docx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { FC, useMemo, useRef, useState } from "react";

import MMVDLogoSrc from "../../assets/logos/mmvd-logo.png";
import { useNotification } from "../../components/FeedbackNotification/useNotification";
import { useDownloadJob } from "../../hooks/useDownloadReport";
import { useAppSelector } from "../../redux";
import downloadServices from "../../redux/services/download.services";
import {
	useGetInactiveRegionsQuery,
	useGetRegionalSummaryQuery,
	useGetTopViolationsQuery
} from "../../redux/reducers/dashboard.reducer";
import { IGetReportsRequestData } from "../../types";
import { convertSnakeToTitleCase, formatNumberMetric } from "../../utils/commonUtils";
import { NON_HSRP_VIOLATION_NAMES, TIME_FILTER_FORMAT } from "../../utils/constants";
import ActionBar from "./ActionBar";
import type { FilterOptionValue } from "./FilterPopup/FilterDropdown";
import FilterPopup from "./FilterPopup/FilterPopup";
import KPICard from "./KPICard";
import RegionalBarChart from "./RegionalBarChart";
import RegionalSummaryTable from "./RegionalSummaryTable";
import ViolationBarChart from "./ViolationBarChart";

interface IInterceptorDataItem {
	id: number;
	numberplate: string;
	location: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
}

interface IRegionDataItem {
	id: number;
	region: string;
	approved: number;
	pending: number;
	rejected: number;
	total: number;
	interceptors: IInterceptorDataItem[];
}

interface IViolationDataItem {
	type: string;
	count: number;
	percentage: number;
}

interface IFilteredData {
	totalApproved: number;
	totalPending: number;
	totalRejected: number;
	regions: IRegionDataItem[];
	interceptors: IInterceptorDataItem[];
	violations: IViolationDataItem[];
}

const Reports: FC = () => {
	const filtersState = useAppSelector((state) => state.filters);
	const authState = useAppSelector((state) => state.auth);
	const { notify } = useNotification();
	const [showFilterPopup, setShowFilterPopup] = useState<boolean>(false);
	const [selectedRegions, setSelectedRegions] = useState<FilterOptionValue[]>([]);
	const [selectedViolations, setSelectedViolations] = useState<FilterOptionValue[]>([]);
	const [selectedInterceptors, setSelectedInterceptors] = useState<FilterOptionValue[]>([]);
	const { isLoading: isExporting, startDownload } = useDownloadJob();
	const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
	const [isGeneratingWord, setIsGeneratingWord] = useState<boolean>(false);
	const [isExportingNicApiUsage, setIsExportingNicApiUsage] = useState<boolean>(false);
	const filterButtonRef = useRef<HTMLButtonElement | null>(null);
	const pdfPrintRef = useRef<HTMLDivElement>(null);
	const logoRef = useRef<HTMLDivElement>(null);
	const kpiCardsRef = useRef<HTMLDivElement>(null);
	const kpiIcon1Ref = useRef<HTMLDivElement>(null);
	const kpiIcon2Ref = useRef<HTMLDivElement>(null);
	const kpiIcon3Ref = useRef<HTMLDivElement>(null);
	const kpiIcon4Ref = useRef<HTMLDivElement>(null);
	const regionalChartRef = useRef<HTMLDivElement>(null);
	const violationsChartRef = useRef<HTMLDivElement>(null);

	// Calculate filter count
	const filterCount = useMemo<number>(() => {
		return selectedRegions.length + selectedViolations.length + selectedInterceptors.length;
	}, [selectedRegions.length, selectedViolations.length, selectedInterceptors.length]);

	// Build API request parameters - API handles all filtering
	const apiParams = useMemo<IGetReportsRequestData>(() => {
		const params: IGetReportsRequestData = {};

		// Date and time range from filters state
		if (filtersState.timeFilter?.start_time && filtersState.timeFilter?.end_time) {
			params.start_date = moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format(TIME_FILTER_FORMAT);
			params.end_date = moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format(TIME_FILTER_FORMAT);
		}

		// Violation types filter - comma-separated string
		if (selectedViolations.length > 0) {
			params.violation_type = selectedViolations.map(String).join(",");
		}

		// Jurisdiction filter - comma-separated string
		if (selectedRegions.length > 0) {
			params.jurisdiction_id = selectedRegions.map(String).join(",");
		}

		// Interceptor filter - comma-separated string
		if (selectedInterceptors.length > 0) {
			params.interceptor_id = selectedInterceptors.map(String).join(",");
		}

		return params;
	}, [filtersState.timeFilter, selectedRegions, selectedViolations, selectedInterceptors]);

	// API calls
	const {
		data: regionalSummaryResponse,
		isLoading: regionalSummaryLoading,
		isFetching: regionalSummaryFetching
	} = useGetRegionalSummaryQuery(apiParams);
	const {
		data: topViolationsResponse,
		isLoading: topViolationsLoading,
		isFetching: topViolationsFetching
	} = useGetTopViolationsQuery(apiParams);
	const { data: inactiveRegionsResponse } = useGetInactiveRegionsQuery(apiParams);

	// Combined loading state
	const isLoading = regionalSummaryLoading || topViolationsLoading;
	const isFetching = regionalSummaryFetching || topViolationsFetching;
	const isDataLoading = isLoading || isFetching;

	// Transform API responses to component data structure
	const filteredData = useMemo<IFilteredData>(() => {
		// Initialize with empty data
		const defaultData: IFilteredData = {
			totalApproved: 0,
			totalPending: 0,
			totalRejected: 0,
			regions: [],
			interceptors: [],
			violations: []
		};

		// If no API responses yet, return empty data
		if (!regionalSummaryResponse && !topViolationsResponse) {
			return defaultData;
		}

		// Transform regional summary to regions data with interceptors
		const regions: IRegionDataItem[] =
			regionalSummaryResponse?.data && Array.isArray(regionalSummaryResponse.data)
				? regionalSummaryResponse.data.map((item) => ({
						id: item.jurisdiction_id,
						region: item.jurisdiction_name,
						approved: item.approved_count,
						pending: item.pending_count,
						rejected: item.rejected_count,
						total: item.total,
						interceptors: Array.isArray(item.interceptors)
							? item.interceptors.map((int) => ({
									id: int.interceptor_id,
									numberplate: int.numberplate,
									location: int.location_name,
									approved: int.approved_count,
									pending: int.pending_count,
									rejected: int.rejected_count,
									total: int.total
							  }))
							: []
				  }))
				: [];

		// Transform violations data and calculate percentages
		const violations: IViolationDataItem[] =
			topViolationsResponse?.data && Array.isArray(topViolationsResponse.data)
				? (() => {
						const totalCount = topViolationsResponse.data.reduce((sum, item) => sum + item.count, 0);
						return topViolationsResponse.data.map((item) => ({
							type: NON_HSRP_VIOLATION_NAMES.includes(item.violation_type_name)
								? "Non HSRP"
								: convertSnakeToTitleCase(item.violation_type_name),
							count: item.count,
							percentage: totalCount > 0 ? (item.count / totalCount) * 100 : 0
						}));
				  })()
				: [];

		// Interceptor data is now nested within regions
		const interceptors: IInterceptorDataItem[] = [];

		// Calculate totals from regions data (API doesn't provide totals at top level)
		const totalApproved = regions.reduce((sum, r) => sum + r.approved, 0);
		const totalPending = regions.reduce((sum, r) => sum + r.pending, 0);
		const totalRejected = regions.reduce((sum, r) => sum + r.rejected, 0);

		return {
			totalApproved,
			totalPending,
			totalRejected,
			regions,
			interceptors,
			violations
		};
	}, [regionalSummaryResponse, topViolationsResponse]);

	const inactiveRegions = useMemo<IRegionDataItem[]>(() => {
		if (!inactiveRegionsResponse?.data || !Array.isArray(inactiveRegionsResponse.data)) return [];
		return inactiveRegionsResponse.data.map((item) => ({
			id: item.jurisdiction_id,
			region: item.jurisdiction_name,
			approved: item.approved_count,
			pending: item.pending_count,
			rejected: item.rejected_count,
			total: item.total,
			interceptors: Array.isArray(item.interceptors)
				? item.interceptors.map((int) => ({
						id: int.interceptor_id,
						numberplate: int.numberplate,
						location: int.location_name,
						approved: int.approved_count,
						pending: int.pending_count,
						rejected: int.rejected_count,
						total: int.total
				  }))
				: []
		}));
	}, [inactiveRegionsResponse]);

	const handleFilterClose = () => {
		setShowFilterPopup(false);
	};

	const handleFilterClick = () => {
		setShowFilterPopup(!showFilterPopup);
	};

	const handleExportExcel = (format: "excel" | "csv") => {
		// Build export request parameters - same as API params, API handles all filtering.
		const exportParams: IGetReportsRequestData = {};

		// Date and time range from filters state.
		if (filtersState.timeFilter?.start_time && filtersState.timeFilter?.end_time) {
			exportParams.start_date = moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format(
				TIME_FILTER_FORMAT
			);
			exportParams.end_date = moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format(TIME_FILTER_FORMAT);
		}

		// Violation types filter - comma-separated string.
		if (selectedViolations.length > 0) {
			exportParams.violation_type = selectedViolations.map(String).join(",");
		}

		// Jurisdiction filter - comma-separated string.
		if (selectedRegions.length > 0) {
			exportParams.jurisdiction_id = selectedRegions.map(String).join(",");
		}

		// Interceptor filter - comma-separated string.
		if (selectedInterceptors.length > 0) {
			exportParams.interceptor_id = selectedInterceptors.map(String).join(",");
		}

		startDownload({
			report_type: "challan_summary",
			report_format: format,
			job_meta_data: {
				filters: exportParams
			}
		});
	};

	const handleExportNicApiUsage = async () => {
		// Backend defaults to the current month when no dates are sent. Dates are inclusive, in IST.
		const requestData: { start_date?: string; end_date?: string } = {};

		if (filtersState.timeFilter?.start_time && filtersState.timeFilter?.end_time) {
			const startDate = moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT);
			const endDate = moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT);

			// Backend caps this export at 62 days.
			if (endDate.clone().startOf("day").diff(startDate.clone().startOf("day"), "days") + 1 > 62) {
				notify("NIC API usage export supports a maximum of 62 days", "error");
				return;
			}

			requestData.start_date = startDate.format("YYYY-MM-DD");
			requestData.end_date = endDate.format("YYYY-MM-DD");
		}

		setIsExportingNicApiUsage(true);
		try {
			await downloadServices.handleDownloadNicApiUsage(requestData);
			notify("NIC API usage report downloaded successfully", "success");
		} catch (error) {
			notify(typeof error === "string" ? error : "Failed to download NIC API usage report", "error");
		} finally {
			setIsExportingNicApiUsage(false);
		}
	};

	const handleExportPDF = async () => {
		setIsGeneratingPDF(true);
		try {
			// Let React mount the hidden export DOM before capturing
			await new Promise((resolve) => setTimeout(resolve, 100));
			if (!pdfPrintRef.current) {
				setIsGeneratingPDF(false);
				return;
			}

			const pdf = new jsPDF("p", "mm", "a4") as jsPDF & { lastAutoTable?: { finalY: number } };
			const pdfWidth = pdf.internal.pageSize.getWidth();
			const pdfHeight = pdf.internal.pageSize.getHeight();

			// Define consistent margins for all pages
			const margins = {
				top: 10,
				right: 10,
				bottom: 10,
				left: 10
			};

			// Calculate available content area (accounting for header and footer)
			const headerHeight = 25;
			const footerHeight = 15;
			const contentWidth = pdfWidth - margins.left - margins.right;
			const contentHeight = pdfHeight - margins.top - margins.bottom - headerHeight - footerHeight;

			// Prepare header and footer information
			const reportGeneratedTime = moment().format("DD MMM YYYY, hh:mm:ss A");
			const dateRange = `${moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format(
				"DD MMM YYYY, hh:mm:ss A"
			)} to ${moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("DD MMM YYYY, hh:mm:ss A")}`;
			const userName = authState.user?.full_name || authState.user?.username || authState.user?.email || "User";

			// Build applied filters string
			const appliedFilters: string[] = [];
			if (selectedRegions.length > 0) {
				appliedFilters.push(`Regions: ${selectedRegions.length} selected`);
			}
			if (selectedViolations.length > 0) {
				appliedFilters.push(`Violations: ${selectedViolations.length} selected`);
			}
			if (selectedInterceptors.length > 0) {
				appliedFilters.push(`Interceptors: ${selectedInterceptors.length} selected`);
			}
			const filtersText = appliedFilters.length > 0 ? appliedFilters.join(" | ") : "No filters applied";

			// Wait for charts to fully render with labels
			await new Promise((resolve) => setTimeout(resolve, 1000));

			// Capture logo as image from the ref
			let logoBase64 = "";
			if (logoRef.current) {
				try {
					const logoCanvas = await html2canvas(logoRef.current, {
						scale: 4,
						backgroundColor: "#f9fafb",
						logging: false,
						useCORS: true
					});
					logoBase64 = logoCanvas.toDataURL("image/png", 1.0);
				} catch (error) {
					console.error("Error capturing logo:", error);
				}
			}

			// Function to add header to a page
			const addHeader = (pageNum: number) => {
				// Header background
				pdf.setFillColor(249, 250, 251);
				pdf.rect(0, 0, pdfWidth, margins.top + headerHeight, "F");

				// Add logo on the left
				if (logoBase64) {
					try {
						const logoWidth = 90;
						const logoHeight = 21.6;
						const logoX = margins.left;
						const logoY = margins.top;

						pdf.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
					} catch (error) {
						console.error("Error adding logo to PDF:", error);
					}
				}

				// Details on the right side
				const rightX = pdfWidth - margins.right;

				// Report name (right aligned)
				pdf.setFontSize(14);
				pdf.setFont("helvetica", "bold");
				pdf.setTextColor(0, 0, 0);
				const reportTitle = "Challan Reports";
				const reportTitleWidth = pdf.getTextWidth(reportTitle);
				pdf.text(reportTitle, rightX - reportTitleWidth, margins.top + 6);

				// Date range (right aligned)
				pdf.setFontSize(9);
				pdf.setFont("helvetica", "normal");
				pdf.setTextColor(60, 60, 60);
				const dateText = dateRange;
				const dateTextWidth = pdf.getTextWidth(dateText);
				pdf.text(dateText, rightX - dateTextWidth, margins.top + 12);

				// Applied filters (right aligned)
				pdf.setFontSize(8);
				pdf.setTextColor(100, 100, 100);
				const filterText = `Filters: ${filtersText}`;
				const filterTextWidth = pdf.getTextWidth(filterText);
				pdf.text(filterText, rightX - filterTextWidth, margins.top + 18);

				// Separator line
				pdf.setDrawColor(224, 224, 224);
				pdf.setLineWidth(0.5);
				pdf.line(margins.left, margins.top + headerHeight, pdfWidth - margins.right, margins.top + headerHeight);
			};

			// Function to add footer to a page
			const addFooter = (pageNum: number, totalPages: number) => {
				const footerY = pdfHeight - margins.bottom - 10;

				// Separator line
				pdf.setDrawColor(224, 224, 224);
				pdf.setLineWidth(0.5);
				pdf.line(margins.left, footerY - 3, pdfWidth - margins.right, footerY - 3);

				// Generated info (left)
				pdf.setFontSize(8);
				pdf.setFont("helvetica", "normal");
				pdf.setTextColor(100, 100, 100);
				pdf.text(`Generated by: ${userName}`, margins.left, footerY + 2);
				pdf.text(`Generated on: ${reportGeneratedTime}`, margins.left, footerY + 7);

				// Page number (right)
				pdf.setFontSize(9);
				pdf.setFont("helvetica", "normal");
				const pageText = `Page ${pageNum} of ${totalPages}`;
				const pageTextWidth = pdf.getTextWidth(pageText);
				pdf.text(pageText, pdfWidth - margins.right - pageTextWidth, footerY + 5);
			};

			// Track current page and vertical position
			let pageNumber = 1;
			addHeader(pageNumber);
			let currentY = margins.top + headerHeight + 6;

			const ensureSpace = (blockHeight: number) => {
				const maxContentY = pdfHeight - margins.bottom - footerHeight;
				if (currentY + blockHeight > maxContentY) {
					pdf.addPage();
					pageNumber += 1;
					addHeader(pageNumber);
					currentY = margins.top + headerHeight + 6;
				}
			};

			// KPI cards as image (same look as UI)
			const chartCanvasOptions = {
				scale: 4,
				useCORS: true,
				backgroundColor: "#ffffff",
				logging: false,
				allowTaint: true,
				windowWidth: 1200,
				windowHeight: window.innerHeight
			} as const;

			if (kpiCardsRef.current) {
				const kpiCanvas = await html2canvas(kpiCardsRef.current, chartCanvasOptions);
				const kpiImgData = kpiCanvas.toDataURL("image/png", 1.0);
				const kpiWidth = kpiCanvas.width;
				const kpiHeight = kpiCanvas.height;
				const kpiRatio = contentWidth / kpiWidth;
				const kpiScaledWidth = contentWidth;
				const kpiScaledHeight = kpiHeight * kpiRatio;
				const kpiImgX = margins.left;
				ensureSpace(kpiScaledHeight + 4);
				pdf.addImage(kpiImgData, "PNG", kpiImgX, currentY, kpiScaledWidth, kpiScaledHeight, undefined, "FAST");
				currentY += kpiScaledHeight + 8;
			}

			// Capture charts separately as images
			const chartMaxWidth = contentWidth;
			const chartMaxHeight = contentHeight / 2;

			const addChartImage = async (chartRef: React.RefObject<HTMLDivElement>, title: string) => {
				if (!chartRef.current) return;

				const chartCanvas = await html2canvas(chartRef.current, chartCanvasOptions);
				const chartImgData = chartCanvas.toDataURL("image/png", 1.0);
				const chartWidth = chartCanvas.width;
				const chartHeight = chartCanvas.height;
				const chartRatio = Math.min(chartMaxWidth / chartWidth, chartMaxHeight / chartHeight);
				const scaledWidth = chartWidth * chartRatio;
				const scaledHeight = chartHeight * chartRatio;
				const imgX = margins.left + (contentWidth - scaledWidth) / 2;

				// Title
				pdf.setFont("helvetica", "bold");
				pdf.setFontSize(12);
				ensureSpace(10 + scaledHeight);
				pdf.text(title, margins.left, currentY);
				currentY += 6;

				// Image
				pdf.addImage(chartImgData, "PNG", imgX, currentY, scaledWidth, scaledHeight, undefined, "FAST");
				currentY += scaledHeight + 6;
			};

			await addChartImage(regionalChartRef, "Regional Performance");
			await addChartImage(violationsChartRef, "Top Violations");

			// Make sure there is enough space for the table; if not, go to a new page
			const minTableSpace = 60;
			ensureSpace(minTableSpace);
			const startY = currentY;

			// Prepare table data from filteredData.regions including interceptors
			const sortedRegions = [...filteredData.regions].sort((a, b) => b.total - a.total);
			const tableData: any[] = [];

			sortedRegions.forEach((region) => {
				// Add region row (bold)
				tableData.push([
					{
						content: region.region,
						styles: { fontStyle: "bold" }
					},
					{
						content: formatNumberMetric(region.approved),
						styles: { fontStyle: "bold" }
					},
					{
						content: formatNumberMetric(region.pending),
						styles: { fontStyle: "bold" }
					},
					{
						content: formatNumberMetric(region.rejected),
						styles: { fontStyle: "bold" }
					},
					{
						content: formatNumberMetric(region.total),
						styles: { fontStyle: "bold" }
					},
					{
						content: region.total > 0 ? `${formatNumberMetric((region.approved / region.total) * 100, 1)}%` : "0.0%",
						styles: {
							fontStyle: "bold",
							textColor: (region.total > 0 && region.approved / region.total >= 0.5
								? [5, 150, 105]
								: [217, 119, 6]) as [number, number, number]
						}
					}
				]);

				// Add interceptor rows (indented, lighter background)
				if (region.interceptors && region.interceptors.length > 0) {
					region.interceptors.forEach((interceptor) => {
						const rate = interceptor.total > 0 ? interceptor.approved / interceptor.total : 0;
						tableData.push([
							{
								content: `     ${interceptor.numberplate} - ${interceptor.location}`,
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8,
									textColor: [100, 100, 100]
								}
							},
							{
								content: formatNumberMetric(interceptor.approved),
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8
								}
							},
							{
								content: formatNumberMetric(interceptor.pending),
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8
								}
							},
							{
								content: formatNumberMetric(interceptor.rejected),
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8
								}
							},
							{
								content: formatNumberMetric(interceptor.total),
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8
								}
							},
							{
								content: interceptor.total > 0 ? `${formatNumberMetric(rate * 100, 1)}%` : "0.0%",
								styles: {
									fillColor: [245, 245, 245],
									fontSize: 8,
									textColor: (rate >= 0.5 ? [5, 150, 105] : [217, 119, 6]) as [number, number, number]
								}
							}
						]);
					});
				}
			});

			// Inactive regions section: separator row then inactive region rows
			if (inactiveRegions.length > 0) {
				tableData.push([
					{
						content: "Inactive Regions",
						styles: {
							fillColor: [220, 222, 226],
							fontStyle: "bold",
							fontSize: 10,
							textColor: [70, 70, 75]
						}
					},
					{ content: "", styles: { fillColor: [220, 222, 226] } },
					{ content: "", styles: { fillColor: [220, 222, 226] } },
					{ content: "", styles: { fillColor: [220, 222, 226] } },
					{ content: "", styles: { fillColor: [220, 222, 226] } },
					{ content: "", styles: { fillColor: [220, 222, 226] } }
				]);
				const sortedInactive = [...inactiveRegions].sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
				sortedInactive.forEach((region) => {
					const rApproved = region.approved ?? 0;
					const rPending = region.pending ?? 0;
					const rRejected = region.rejected ?? 0;
					const rTotal = region.total ?? 0;
					tableData.push([
						{
							content: region.region ?? "",
							styles: { fontStyle: "bold", fillColor: [250, 250, 250] }
						},
						{
							content: formatNumberMetric(rApproved),
							styles: { fontStyle: "bold", fillColor: [250, 250, 250] }
						},
						{
							content: formatNumberMetric(rPending),
							styles: { fontStyle: "bold", fillColor: [250, 250, 250] }
						},
						{
							content: formatNumberMetric(rRejected),
							styles: { fontStyle: "bold", fillColor: [250, 250, 250] }
						},
						{
							content: formatNumberMetric(rTotal),
							styles: { fontStyle: "bold", fillColor: [250, 250, 250] }
						},
						{
							content: rTotal > 0 ? `${formatNumberMetric((rApproved / rTotal) * 100, 1)}%` : "0.0%",
							styles: {
								fontStyle: "bold",
								fillColor: [250, 250, 250],
								textColor:
									rTotal > 0 && rApproved / rTotal >= 0.5
										? ([5, 150, 105] as [number, number, number])
										: ([217, 119, 6] as [number, number, number])
							}
						}
					]);
					if (region.interceptors && region.interceptors.length > 0) {
						region.interceptors.forEach((interceptor) => {
							const iApproved = interceptor.approved ?? 0;
							const iTotal = interceptor.total ?? 0;
							const rate = iTotal > 0 ? iApproved / iTotal : 0;
							tableData.push([
								{
									content: `     ${interceptor.numberplate ?? ""} - ${interceptor.location ?? ""}`,
									styles: {
										fillColor: [245, 245, 245],
										fontSize: 8,
										textColor: [100, 100, 100]
									}
								},
								{
									content: formatNumberMetric(interceptor.approved ?? 0),
									styles: { fillColor: [245, 245, 245], fontSize: 8 }
								},
								{
									content: formatNumberMetric(interceptor.pending ?? 0),
									styles: { fillColor: [245, 245, 245], fontSize: 8 }
								},
								{
									content: formatNumberMetric(interceptor.rejected ?? 0),
									styles: { fillColor: [245, 245, 245], fontSize: 8 }
								},
								{
									content: formatNumberMetric(interceptor.total ?? 0),
									styles: { fillColor: [245, 245, 245], fontSize: 8 }
								},
								{
									content: iTotal > 0 ? `${formatNumberMetric(rate * 100, 1)}%` : "0.0%",
									styles: {
										fillColor: [245, 245, 245],
										fontSize: 8,
										textColor: (rate >= 0.5 ? [5, 150, 105] : [217, 119, 6]) as [number, number, number]
									}
								}
							]);
						});
					}
				});
			}

			// Add title before table
			const titleY = startY;
			pdf.setFontSize(14);
			pdf.setFont("helvetica", "bold");
			pdf.setTextColor(0, 0, 0);
			pdf.text("Regional Summary", margins.left, titleY);

			// Add table using autoTable with headers that repeat on each page
			autoTable(pdf, {
				head: [["Region", "Issued", "Pending", "Rejected", "Total", "Approval Rate"]],
				body: tableData,
				startY: titleY + 8,
				theme: "plain",
				styles: {
					fontSize: 9,
					cellPadding: 4,
					lineWidth: 0.1,
					lineColor: [240, 240, 240]
				},
				headStyles: {
					fillColor: [249, 250, 251],
					textColor: [0, 0, 0],
					fontStyle: "bold",
					fontSize: 10,
					lineWidth: 0.5,
					lineColor: [224, 224, 224]
				},
				bodyStyles: {
					fillColor: [255, 255, 255],
					textColor: [0, 0, 0],
					fontStyle: "normal",
					fontSize: 9
				},
				alternateRowStyles: {
					fillColor: [249, 250, 251]
				},
				columnStyles: {
					0: { halign: "left", cellWidth: "auto" },
					1: { halign: "right", cellWidth: 25 },
					2: { halign: "right", cellWidth: 25 },
					3: { halign: "right", cellWidth: 25 },
					4: { halign: "right", cellWidth: 25 },
					5: { halign: "right", cellWidth: 35 }
				},
				margin: {
					top: margins.top + headerHeight,
					right: margins.right,
					bottom: margins.bottom + footerHeight,
					left: margins.left
				},
				pageBreak: "auto",
				showHead: "everyPage",
				willDrawCell: (data) => {
					// Add hover effect styling similar to browser
					if (data.section === "body") {
						data.cell.styles.lineWidth = 0.1;
						data.cell.styles.lineColor = [224, 224, 224];
					}
				},
				didDrawPage: (data) => {
					const currentPageNum = pdf.internal.pages.length - 1;

					// Add header on table continuation pages (pages after the first table page)
					if (data.pageNumber > 1) {
						addHeader(currentPageNum);
					}
				}
			});

			// Add footers to all pages
			const totalPages = pdf.internal.pages.length - 1;
			for (let i = 1; i <= totalPages; i++) {
				pdf.setPage(i);
				addFooter(i, totalPages);
			}

			// Release large image data so GC can reclaim sooner
			logoBase64 = "";

			const fileName = `Reports_${moment().format("YYYY-MM-DD_HH-mm-ss")}.pdf`;
			pdf.save(fileName);
			notify("PDF exported successfully", "success");
		} catch (error) {
			console.error("Error generating PDF:", error);
			notify("Failed to generate PDF", "error");
		} finally {
			setIsGeneratingPDF(false);
		}
	};

	const handleExportWord = async () => {
		setIsGeneratingWord(true);
		try {
			// Let React mount the hidden export DOM before capturing
			await new Promise((resolve) => setTimeout(resolve, 100));
			if (!pdfPrintRef.current) {
				setIsGeneratingWord(false);
				return;
			}

			// Prepare header and footer information
			const reportGeneratedTime = moment().format("DD MMM YYYY, hh:mm:ss A");
			const dateRange = `${moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format(
				"DD MMM YYYY, hh:mm:ss A"
			)} to ${moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("DD MMM YYYY, hh:mm:ss A")}`;
			const userName = authState.user?.full_name || authState.user?.username || authState.user?.email || "User";

			// Build applied filters string
			const appliedFilters: string[] = [];
			if (selectedRegions.length > 0) {
				appliedFilters.push(`Regions: ${selectedRegions.length} selected`);
			}
			if (selectedViolations.length > 0) {
				appliedFilters.push(`Violations: ${selectedViolations.length} selected`);
			}
			if (selectedInterceptors.length > 0) {
				appliedFilters.push(`Interceptors: ${selectedInterceptors.length} selected`);
			}
			const filtersText = appliedFilters.length > 0 ? appliedFilters.join(" | ") : "No filters applied";

			// Wait for charts to fully render
			await new Promise((resolve) => setTimeout(resolve, 1000));

			// Capture logo as image
			let logoBase64 = "";
			if (logoRef.current) {
				try {
					const logoCanvas = await html2canvas(logoRef.current, {
						scale: 4,
						backgroundColor: "#f9fafb",
						logging: false,
						useCORS: true
					});
					logoBase64 = logoCanvas.toDataURL("image/png", 1.0);
				} catch (error) {
					console.error("Error capturing logo:", error);
				}
			}

			// Convert base64 to buffer for docx
			const base64ToBuffer = (base64: string) => {
				const base64Data = base64.split(",")[1];
				const binaryString = atob(base64Data);
				const bytes = new Uint8Array(binaryString.length);
				for (let i = 0; i < binaryString.length; i++) {
					bytes[i] = binaryString.charCodeAt(i);
				}
				return bytes;
			};

			// Capture charts separately
			const chartCanvasOptions = {
				scale: 4,
				useCORS: true,
				backgroundColor: "#ffffff",
				logging: false,
				allowTaint: true,
				windowWidth: 1200,
				windowHeight: window.innerHeight
			} as const;

			let regionalChartImg: Uint8Array | null = null;
			let violationsChartImg: Uint8Array | null = null;
			let regionalChartWidth = 600;
			let regionalChartHeight = 300;
			let violationsChartWidth = 600;
			let violationsChartHeight = 300;

			if (regionalChartRef.current) {
				const regionalCanvas = await html2canvas(regionalChartRef.current, chartCanvasOptions);
				regionalChartImg = base64ToBuffer(regionalCanvas.toDataURL("image/png", 1.0));
				regionalChartWidth = regionalCanvas.width;
				regionalChartHeight = regionalCanvas.height;
			}

			if (violationsChartRef.current) {
				const violationsCanvas = await html2canvas(violationsChartRef.current, chartCanvasOptions);
				violationsChartImg = base64ToBuffer(violationsCanvas.toDataURL("image/png", 1.0));
				violationsChartWidth = violationsCanvas.width;
				violationsChartHeight = violationsCanvas.height;
			}

			// Full content width for chart images (A4 ~6.3" content at 96dpi ≈ 605px)
			const wordContentWidthPx = 605;
			const regionalChartHeightPx = Math.round(wordContentWidthPx * (regionalChartHeight / regionalChartWidth));
			const violationsChartHeightPx = Math.round(wordContentWidthPx * (violationsChartHeight / violationsChartWidth));

			// Capture KPI icons for Word (small images to embed in each card)
			const iconCanvasOptions = { scale: 2, useCORS: true, backgroundColor: "transparent", logging: false } as const;
			const iconSizePx = 32;
			let kpiIcon1Img: Uint8Array | null = null;
			let kpiIcon2Img: Uint8Array | null = null;
			let kpiIcon3Img: Uint8Array | null = null;
			let kpiIcon4Img: Uint8Array | null = null;
			if (kpiIcon1Ref.current) {
				const c = await html2canvas(kpiIcon1Ref.current, iconCanvasOptions);
				kpiIcon1Img = base64ToBuffer(c.toDataURL("image/png", 1.0));
			}
			if (kpiIcon2Ref.current) {
				const c = await html2canvas(kpiIcon2Ref.current, iconCanvasOptions);
				kpiIcon2Img = base64ToBuffer(c.toDataURL("image/png", 1.0));
			}
			if (kpiIcon3Ref.current) {
				const c = await html2canvas(kpiIcon3Ref.current, iconCanvasOptions);
				kpiIcon3Img = base64ToBuffer(c.toDataURL("image/png", 1.0));
			}
			if (kpiIcon4Ref.current) {
				const c = await html2canvas(kpiIcon4Ref.current, iconCanvasOptions);
				kpiIcon4Img = base64ToBuffer(c.toDataURL("image/png", 1.0));
			}

			// Prepare table data from filteredData.regions
			const sortedRegions = [...filteredData.regions].sort((a, b) => b.total - a.total);

			// Create table rows for regional summary
			const tableRows: TableRow[] = [];

			// Header row
			tableRows.push(
				new TableRow({
					children: [
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Region", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						}),
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Issued", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						}),
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Pending", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						}),
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Rejected", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						}),
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Total", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						}),
						new TableCell({
							children: [new Paragraph({ children: [new TextRun({ text: "Approval Rate", bold: true })] })],
							shading: { fill: "f9fafb" },
							margins: { top: 100, bottom: 100, left: 100, right: 100 }
						})
					]
				})
			);

			// Data rows
			sortedRegions.forEach((region) => {
				// Region row
				const approvalRate = region.total > 0 ? formatNumberMetric((region.approved / region.total) * 100, 1) : "0.0";
				tableRows.push(
					new TableRow({
						children: [
							new TableCell({
								children: [new Paragraph({ children: [new TextRun({ text: region.region, bold: true })] })],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [
									new Paragraph({ children: [new TextRun({ text: formatNumberMetric(region.approved), bold: true })] })
								],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ children: [new TextRun({ text: formatNumberMetric(region.pending), bold: true })] })],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [
									new Paragraph({ children: [new TextRun({ text: formatNumberMetric(region.rejected), bold: true })] })
								],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ children: [new TextRun({ text: formatNumberMetric(region.total), bold: true })] })],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ children: [new TextRun({ text: `${approvalRate}%`, bold: true })] })],
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							})
						]
					})
				);

				// Interceptor rows
				if (region.interceptors && region.interceptors.length > 0) {
					region.interceptors.forEach((interceptor) => {
						const rate = interceptor.total > 0 ? formatNumberMetric((interceptor.approved / interceptor.total) * 100, 1) : "0.0";
						tableRows.push(
							new TableRow({
								children: [
									new TableCell({
										children: [new Paragraph({ text: `     ${interceptor.numberplate} - ${interceptor.location}` })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									}),
									new TableCell({
										children: [new Paragraph({ text: formatNumberMetric(interceptor.approved) })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									}),
									new TableCell({
										children: [new Paragraph({ text: formatNumberMetric(interceptor.pending) })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									}),
									new TableCell({
										children: [new Paragraph({ text: formatNumberMetric(interceptor.rejected) })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									}),
									new TableCell({
										children: [new Paragraph({ text: formatNumberMetric(interceptor.total) })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									}),
									new TableCell({
										children: [new Paragraph({ text: `${rate}%` })],
										shading: { fill: "f5f5f5" },
										margins: { top: 100, bottom: 100, left: 100, right: 100 }
									})
								]
							})
						);
					});
				}
			});

			// Inactive regions section: separator row then inactive region rows
			if (inactiveRegions.length > 0) {
				tableRows.push(
					new TableRow({
						children: [
							new TableCell({
								children: [
									new Paragraph({
										children: [new TextRun({ text: "Inactive Regions", bold: true })]
									})
								],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ text: "" })],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ text: "" })],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ text: "" })],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ text: "" })],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							}),
							new TableCell({
								children: [new Paragraph({ text: "" })],
								shading: { fill: "dce0e6" },
								margins: { top: 100, bottom: 100, left: 100, right: 100 }
							})
						]
					})
				);
				const sortedInactive = [...inactiveRegions].sort((a, b) => (b.total ?? 0) - (a.total ?? 0));
				sortedInactive.forEach((region) => {
					const rTotal = region.total ?? 0;
					const rApproved = region.approved ?? 0;
					const approvalRate = rTotal > 0 ? formatNumberMetric((rApproved / rTotal) * 100, 1) : "0.0";
					tableRows.push(
						new TableRow({
							children: [
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: String(region.region ?? ""), bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: formatNumberMetric(region.approved ?? 0), bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: formatNumberMetric(region.pending ?? 0), bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: formatNumberMetric(region.rejected ?? 0), bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: formatNumberMetric(region.total ?? 0), bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: `${approvalRate}%`, bold: true })]
										})
									],
									shading: { fill: "fafafa" },
									margins: { top: 100, bottom: 100, left: 100, right: 100 }
								})
							]
						})
					);
					if (region.interceptors && region.interceptors.length > 0) {
						region.interceptors.forEach((interceptor) => {
							const iTotal = interceptor.total ?? 0;
							const iApproved = interceptor.approved ?? 0;
							const rate = iTotal > 0 ? (iApproved / iTotal) * 100 : 0;
							const rateStr = iTotal > 0 ? formatNumberMetric(rate, 1) : "0.0";
							tableRows.push(
								new TableRow({
									children: [
										new TableCell({
											children: [
												new Paragraph({
													text: `     ${interceptor.numberplate ?? ""} - ${interceptor.location ?? ""}`
												})
											],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										}),
										new TableCell({
											children: [new Paragraph({ text: formatNumberMetric(interceptor.approved ?? 0) })],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										}),
										new TableCell({
											children: [new Paragraph({ text: formatNumberMetric(interceptor.pending ?? 0) })],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										}),
										new TableCell({
											children: [new Paragraph({ text: formatNumberMetric(interceptor.rejected ?? 0) })],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										}),
										new TableCell({
											children: [new Paragraph({ text: formatNumberMetric(interceptor.total ?? 0) })],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										}),
										new TableCell({
											children: [new Paragraph({ text: `${rateStr}%` })],
											shading: { fill: "f5f5f5" },
											margins: { top: 100, bottom: 100, left: 100, right: 100 }
										})
									]
								})
							);
						});
					}
				});
			}

			// Build document sections
			const documentChildren: any[] = [];

			// Header: logo left, report details right (single-row table)
			const headerTableLeftCellChildren: any[] = [];
			if (logoBase64) {
				try {
					headerTableLeftCellChildren.push(
						new Paragraph({
							children: [
								new ImageRun({
									type: "png",
									data: base64ToBuffer(logoBase64),
									transformation: { width: 360, height: 86 }
								} as any)
							]
						})
					);
				} catch (error) {
					console.error("Error adding logo to Word:", error);
				}
			}
			documentChildren.push(
				new Table({
					width: { size: 100, type: WidthType.PERCENTAGE },
					borders: {
						top: { style: BorderStyle.NONE },
						bottom: { style: BorderStyle.NONE },
						left: { style: BorderStyle.NONE },
						right: { style: BorderStyle.NONE },
						insideHorizontal: { style: BorderStyle.NONE },
						insideVertical: { style: BorderStyle.NONE }
					},
					rows: [
						new TableRow({
							children: [
								new TableCell({
									children: headerTableLeftCellChildren.length
										? headerTableLeftCellChildren
										: [new Paragraph({ text: "" })],
									width: { size: 40, type: WidthType.PERCENTAGE },
									margins: { top: 100, bottom: 100, left: 100, right: 200 },
									shading: { fill: "f9fafb" },
									verticalAlign: VerticalAlignTable.CENTER
								}),
								new TableCell({
									children: [
										new Paragraph({
											text: "Challan Reports",
											alignment: AlignmentType.RIGHT,
											heading: HeadingLevel.HEADING_1,
											spacing: { after: 80 }
										}),
										new Paragraph({
											text: dateRange,
											alignment: AlignmentType.RIGHT,
											spacing: { after: 80 }
										}),
										new Paragraph({
											text: `Filters: ${filtersText}`,
											alignment: AlignmentType.RIGHT,
											spacing: { after: 100 }
										})
									],
									width: { size: 60, type: WidthType.PERCENTAGE },
									margins: { top: 100, bottom: 100, left: 200, right: 100 },
									shading: { fill: "f9fafb" },
									verticalAlign: VerticalAlignTable.CENTER
								})
							]
						})
					]
				})
			);
			documentChildren.push(new Paragraph({ text: "", spacing: { after: 200 } }));

			// KPI cards: horizontal layout (icon left, details right), card-style borders (Word has no border-radius)
			const totalChallansWord = filteredData.totalApproved + filteredData.totalPending + filteredData.totalRejected;
			const kpiIconParagraph = (img: Uint8Array): Paragraph =>
				new Paragraph({
					children: [
						new ImageRun({
							type: "png",
							data: img,
							transformation: { width: iconSizePx, height: iconSizePx }
						} as any)
					],
					alignment: AlignmentType.CENTER
				});

			const cardBorder = { style: BorderStyle.SINGLE, size: 1, color: "e5e7eb" };
			const noBorder = { style: BorderStyle.NONE };

			const kpiCardTable = (
				iconImg: Uint8Array | null,
				title: string,
				value: string,
				valueColor: string,
				shading: string
			): Table =>
				new Table({
					width: { size: 100, type: WidthType.PERCENTAGE },
					borders: {
						top: cardBorder,
						bottom: cardBorder,
						left: cardBorder,
						right: cardBorder,
						insideHorizontal: noBorder,
						insideVertical: noBorder
					},
					rows: [
						new TableRow({
							children: [
								new TableCell({
									children: iconImg ? [kpiIconParagraph(iconImg)] : [new Paragraph({ text: "" })],
									width: { size: 18, type: WidthType.PERCENTAGE },
									shading: { fill: shading },
									margins: { top: 150, bottom: 150, left: 150, right: 100 },
									verticalAlign: VerticalAlignTable.CENTER,
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								}),
								new TableCell({
									children: [
										new Paragraph({
											children: [new TextRun({ text: title, bold: true, size: 16 })],
											spacing: { after: 60 }
										}),
										new Paragraph({
											children: [new TextRun({ text: value, bold: true, size: 28, color: valueColor })]
										})
									],
									width: { size: 82, type: WidthType.PERCENTAGE },
									shading: { fill: shading },
									margins: { top: 150, bottom: 150, left: 100, right: 150 },
									verticalAlign: VerticalAlignTable.CENTER,
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								})
							]
						})
					]
				});

			documentChildren.push(
				new Table({
					width: { size: 100, type: WidthType.PERCENTAGE },
					borders: {
						top: noBorder,
						bottom: noBorder,
						left: noBorder,
						right: noBorder,
						insideHorizontal: noBorder,
						insideVertical: noBorder
					},
					cellSpacing: { value: 80, type: "dxa" },
					rows: [
						new TableRow({
							children: [
								new TableCell({
									children: [
										kpiCardTable(kpiIcon1Img, "Total Challans", formatNumberMetric(totalChallansWord), "2563eb", "dbeafe")
									],
									shading: { fill: "ffffff" },
									margins: { top: 0, bottom: 0, left: 0, right: 0 },
									width: { size: 25, type: WidthType.PERCENTAGE },
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								}),
								new TableCell({
									children: [
										kpiCardTable(
											kpiIcon2Img,
											"Challans Issued",
											formatNumberMetric(filteredData.totalApproved),
											"059669",
											"d1fae5"
										)
									],
									shading: { fill: "ffffff" },
									margins: { top: 0, bottom: 0, left: 0, right: 0 },
									width: { size: 25, type: WidthType.PERCENTAGE },
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								}),
								new TableCell({
									children: [
										kpiCardTable(
											kpiIcon3Img,
											"Pending Challans",
											formatNumberMetric(filteredData.totalPending),
											"d97706",
											"fef3c7"
										)
									],
									shading: { fill: "ffffff" },
									margins: { top: 0, bottom: 0, left: 0, right: 0 },
									width: { size: 25, type: WidthType.PERCENTAGE },
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								}),
								new TableCell({
									children: [
										kpiCardTable(
											kpiIcon4Img,
											"Rejected Challans",
											formatNumberMetric(filteredData.totalRejected),
											"b91c1c",
											"fee2e2"
										)
									],
									shading: { fill: "ffffff" },
									margins: { top: 0, bottom: 0, left: 0, right: 0 },
									width: { size: 25, type: WidthType.PERCENTAGE },
									borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder }
								})
							]
						})
					]
				})
			);
			documentChildren.push(new Paragraph({ text: "", spacing: { after: 200 } }));

			// Charts as separate images (full content width, aspect ratio preserved)
			if (regionalChartImg) {
				documentChildren.push(
					new Paragraph({
						text: "Regional Performance",
						heading: HeadingLevel.HEADING_2,
						spacing: { before: 200, after: 100 }
					}),
					new Paragraph({
						children: [
							new ImageRun({
								type: "png",
								data: regionalChartImg,
								transformation: {
									width: wordContentWidthPx,
									height: regionalChartHeightPx
								}
							} as any)
						],
						spacing: { after: 200 }
					})
				);
			}

			if (violationsChartImg) {
				documentChildren.push(
					new Paragraph({
						text: "Top Violations",
						heading: HeadingLevel.HEADING_2,
						spacing: { before: 200, after: 100 }
					}),
					new Paragraph({
						children: [
							new ImageRun({
								type: "png",
								data: violationsChartImg,
								transformation: {
									width: wordContentWidthPx,
									height: violationsChartHeightPx
								}
							} as any)
						],
						spacing: { after: 200 }
					})
				);
			}

			// Regional Summary table title
			documentChildren.push(
				new Paragraph({
					text: "Regional Summary",
					heading: HeadingLevel.HEADING_2,
					spacing: { before: 200, after: 200 }
				})
			);

			// Regional Summary table
			documentChildren.push(
				new Table({
					rows: tableRows,
					width: {
						size: 100,
						type: WidthType.PERCENTAGE
					},
					borders: {
						top: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" },
						bottom: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" },
						left: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" },
						right: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" },
						insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" },
						insideVertical: { style: BorderStyle.SINGLE, size: 1, color: "e0e0e0" }
					}
				})
			);

			// Footer info
			documentChildren.push(
				new Paragraph({
					text: "",
					spacing: { before: 400 }
				}),
				new Paragraph({
					text: `Generated by: ${userName}`,
					spacing: { after: 100 }
				}),
				new Paragraph({
					text: `Generated on: ${reportGeneratedTime}`,
					spacing: { after: 100 }
				})
			);

			// Create document
			const doc = new Document({
				sections: [
					{
						properties: {
							page: {
								margin: {
									top: convertInchesToTwip(0.5),
									right: convertInchesToTwip(0.5),
									bottom: convertInchesToTwip(0.5),
									left: convertInchesToTwip(0.5)
								}
							}
						},
						children: documentChildren
					}
				]
			});

			// Generate and download the document
			const blob = await Packer.toBlob(doc);
			const fileName = `Reports_${moment().format("YYYY-MM-DD_HH-mm-ss")}.docx`;
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = fileName;
			link.click();
			window.URL.revokeObjectURL(url);

			// Release large buffers so GC can reclaim sooner
			logoBase64 = "";
			regionalChartImg = null;
			violationsChartImg = null;
			kpiIcon1Img = null;
			kpiIcon2Img = null;
			kpiIcon3Img = null;
			kpiIcon4Img = null;

			notify("Word document exported successfully", "success");
		} catch (error) {
			console.error("Error generating Word document:", error);
			notify("Failed to generate Word document", "error");
		} finally {
			setIsGeneratingWord(false);
		}
	};

	return (
		<Box>
			<ActionBar
				onFilterClick={handleFilterClick}
				onExportExcel={handleExportExcel}
				onExportPDF={handleExportPDF}
				onExportWord={handleExportWord}
				filterCount={filterCount}
				filterButtonRef={filterButtonRef}
				isExporting={isExporting}
				isGeneratingPDF={isGeneratingPDF}
				isGeneratingWord={isGeneratingWord}
				onExportNicApiUsage={handleExportNicApiUsage}
				isExportingNicApiUsage={isExportingNicApiUsage}
			/>

			<Grid2 container spacing={3} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					{isDataLoading ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 2 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							</CardContent>
						</Card>
					) : (
						<KPICard
							title="Total Challans"
							value={filteredData.totalApproved + filteredData.totalPending + filteredData.totalRejected}
							icon={AssignmentIcon}
							cardColor="#dbeafe"
							iconColor="#3b82f6"
							textColor="#2563eb"
						/>
					)}
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					{isDataLoading ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 2 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							</CardContent>
						</Card>
					) : (
						<KPICard
							title="Challans Issued"
							value={filteredData.totalApproved}
							icon={CheckCircleIcon}
							cardColor="#d1fae5"
							iconColor="#10b981"
							textColor="#059669"
						/>
					)}
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					{isDataLoading ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 2 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							</CardContent>
						</Card>
					) : (
						<KPICard
							title="Pending Challans"
							value={filteredData.totalPending}
							icon={ClockIcon}
							cardColor="#fef3c7"
							iconColor="#f59e0b"
							textColor="#d97706"
						/>
					)}
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
					{isDataLoading ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 2 }}>
								<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
									<Skeleton variant="circular" width={40} height={40} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							</CardContent>
						</Card>
					) : (
						<KPICard
							title="Rejected Challans"
							value={filteredData.totalRejected}
							icon={XCircleIcon}
							cardColor="#fee2e2"
							iconColor="#dc2626"
							textColor="#b91c1c"
						/>
					)}
				</Grid2>
			</Grid2>

			{/* Show Regional Performance Analysis or Interceptor Performance Analysis */}
			<Grid2 container spacing={3} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12 }}>
					{regionalSummaryLoading || regionalSummaryFetching ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
								mb: 3
							}}
						>
							<CardContent sx={{ p: 3, pb: 0, "&:last-child": { pb: 0 } }}>
								<Box sx={{ mb: 2 }}>
									<Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} />
									<Skeleton variant="text" width="60%" height={20} />
								</Box>
								<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
									<CircularProgress />
								</Box>
							</CardContent>
						</Card>
					) : (
						<RegionalBarChart data={filteredData.regions} isInterceptorView={selectedRegions.length === 1} />
					)}
				</Grid2>
			</Grid2>

			<Grid2 container spacing={3} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12 }}>
					{regionalSummaryLoading || regionalSummaryFetching ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 3, pb: 2, "&:last-child": { pb: 2 } }}>
								<Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									{Array.from({ length: 5 }).map((_, idx) => (
										<Box key={idx} sx={{ display: "flex", gap: 2 }}>
											<Skeleton variant="text" width="20%" height={40} />
											<Skeleton variant="text" width="15%" height={40} />
											<Skeleton variant="text" width="15%" height={40} />
											<Skeleton variant="text" width="15%" height={40} />
											<Skeleton variant="text" width="15%" height={40} />
											<Skeleton variant="text" width="15%" height={40} />
										</Box>
									))}
								</Box>
							</CardContent>
						</Card>
					) : (
						<RegionalSummaryTable data={filteredData.regions} inactiveData={inactiveRegions} />
					)}
				</Grid2>
			</Grid2>

			<Grid2 container spacing={3} sx={{ mb: 3 }}>
				<Grid2 size={{ xs: 12 }}>
					{topViolationsLoading || topViolationsFetching ? (
						<Card
							sx={{
								bgcolor: "background.paper",
								border: "1px solid rgba(0, 0, 0, 0.08)",
								borderRadius: 3,
								boxShadow: "0 1px 3px rgba(0,0,0,0.08)"
							}}
						>
							<CardContent sx={{ p: 3, pb: 2 }}>
								<Box sx={{ mb: 3 }}>
									<Skeleton variant="text" width="40%" height={32} sx={{ mb: 0.5 }} />
									<Skeleton variant="text" width="60%" height={20} />
								</Box>
								<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
									<CircularProgress />
								</Box>
							</CardContent>
						</Card>
					) : (
						<ViolationBarChart data={filteredData.violations} />
					)}
				</Grid2>
			</Grid2>

			<FilterPopup
				anchorEl={filterButtonRef.current}
				isOpen={showFilterPopup}
				onClose={handleFilterClose}
				selectedRegions={selectedRegions}
				setSelectedRegions={setSelectedRegions}
				selectedViolations={selectedViolations}
				setSelectedViolations={setSelectedViolations}
				selectedInterceptors={selectedInterceptors}
				setSelectedInterceptors={setSelectedInterceptors}
			/>

			{/* Hidden div for PDF/Word generation - only mounted during export to reduce RAM */}
			{(isGeneratingPDF || isGeneratingWord) && (
				<Box
					ref={pdfPrintRef}
					sx={{
						position: "absolute",
						left: "-9999px",
						top: 0,
						width: "100%",
						maxWidth: "1200px",
						backgroundColor: "#ffffff"
					}}
				>
					<Box ref={kpiCardsRef}>
						<Grid2 container spacing={3} sx={{ mb: 3, p: 2 }}>
							<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
								<KPICard
									title="Total Challans"
									value={filteredData.totalApproved + filteredData.totalPending + filteredData.totalRejected}
									icon={AssignmentIcon}
									cardColor="#dbeafe"
									iconColor="#3b82f6"
									textColor="#2563eb"
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
								<KPICard
									title="Challans Issued"
									value={filteredData.totalApproved}
									icon={CheckCircleIcon}
									cardColor="#d1fae5"
									iconColor="#10b981"
									textColor="#059669"
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
								<KPICard
									title="Pending Challans"
									value={filteredData.totalPending}
									icon={ClockIcon}
									cardColor="#fef3c7"
									iconColor="#f59e0b"
									textColor="#d97706"
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
								<KPICard
									title="Rejected Challans"
									value={filteredData.totalRejected}
									icon={XCircleIcon}
									cardColor="#fee2e2"
									iconColor="#dc2626"
									textColor="#b91c1c"
								/>
							</Grid2>
						</Grid2>
					</Box>

					{/* Hidden KPI icons for Word export (captured as images) */}
					<Box sx={{ position: "absolute", left: "-9999px", display: "flex", gap: 1 }}>
						<Box
							ref={kpiIcon1Ref}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 40,
								height: 40,
								borderRadius: 1.5,
								bgcolor: "#3b82f6"
							}}
						>
							<AssignmentIcon sx={{ color: "white", fontSize: 20 }} />
						</Box>
						<Box
							ref={kpiIcon2Ref}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 40,
								height: 40,
								borderRadius: 1.5,
								bgcolor: "#10b981"
							}}
						>
							<CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
						</Box>
						<Box
							ref={kpiIcon3Ref}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 40,
								height: 40,
								borderRadius: 1.5,
								bgcolor: "#f59e0b"
							}}
						>
							<ClockIcon sx={{ color: "white", fontSize: 20 }} />
						</Box>
						<Box
							ref={kpiIcon4Ref}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: 40,
								height: 40,
								borderRadius: 1.5,
								bgcolor: "#dc2626"
							}}
						>
							<XCircleIcon sx={{ color: "white", fontSize: 20 }} />
						</Box>
					</Box>

					<Grid2 container spacing={3} sx={{ mb: 3, p: 2 }}>
						<Grid2 size={{ xs: 12 }}>
							<Box ref={regionalChartRef}>
								<RegionalBarChart
									data={filteredData.regions}
									isInterceptorView={selectedRegions.length === 1}
									showLabels={true}
								/>
							</Box>
						</Grid2>
					</Grid2>

					<Grid2 container spacing={3} sx={{ mb: 3, p: 2 }}>
						<Grid2 size={{ xs: 12 }}>
							<Box ref={violationsChartRef}>
								<ViolationBarChart data={filteredData.violations} showLabels={true} />
							</Box>
						</Grid2>
					</Grid2>
				</Box>
			)}

			{/* Hidden logo for PDF/Word capture - only mounted during export */}
			{(isGeneratingPDF || isGeneratingWord) && (
				<Box
					ref={logoRef}
					sx={{
						position: "absolute",
						left: "-9999px",
						width: "500px",
						height: "120px",
						display: "flex",
						alignItems: "center",
						gap: "1rem",
						backgroundColor: "#f9fafb",
						padding: "20px"
					}}
				>
					<img src={MMVDLogoSrc} alt="MMVD" height="90px" />
					<Box>
						<Typography fontFamily="Patua One" fontWeight={500} fontSize="36px" whiteSpace="nowrap">
							MTES
						</Typography>
						<Typography variant="body2" fontFamily="Patua One" fontSize="18px" whiteSpace="nowrap">
							Mobile Traffic Enforcement System
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default Reports;
